import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import React, { ChangeEvent, useRef, useState } from "react";
import { saveClubLogo } from "./clubApi";
import { ClubLogo } from "./ClubLogo";
import useClubLogos, { setCachedClubLogo } from "./useClubLogos";

const LOGO_SIZE = 256;
// Must stay below MAX_CLUB_LOGO_LENGTH in playerdatafetch.py.
const MAX_LOGO_LENGTH = 400_000;

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unreadable image"));
    image.src = src;
  });
}

export async function resizeLogo(file: File): Promise<string> {
  const image = await loadImage(await readAsDataUrl(file));
  const width = image.naturalWidth || LOGO_SIZE;
  const height = image.naturalHeight || LOGO_SIZE;
  const scale = Math.min(1, LOGO_SIZE / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas unavailable");
  }
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  let dataUrl = canvas.toDataURL("image/png");
  if (dataUrl.length > MAX_LOGO_LENGTH) {
    // JPEG has no transparency, so flatten onto white before re-encoding.
    context.globalCompositeOperation = "destination-over";
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    dataUrl = canvas.toDataURL("image/jpeg", 0.85);
  }
  if (dataUrl.length > MAX_LOGO_LENGTH) {
    throw new Error("Logo too large");
  }
  return dataUrl;
}

function ClubLogoUpload({
  devPort,
  clubId,
  clubName,
}: {
  devPort: string;
  clubId: number;
  clubName: string;
}) {
  const logos = useClubLogos(devPort, [clubId]);
  const logo = logos[clubId];
  const inputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ severity: "success" | "error"; text: string } | null>(
    null
  );

  function persist(nextLogo: string | null, successText: string) {
    setSaving(true);
    setStatus(null);
    return saveClubLogo(devPort, clubId, nextLogo)
      .then((saved) => {
        if (!saved) {
          throw new Error("Not saved");
        }
        setCachedClubLogo(clubId, nextLogo);
        setStatus({ severity: "success", text: successText });
      })
      .catch(() =>
        setStatus({ severity: "error", text: "We couldn't save the logo. Please try again." })
      )
      .finally(() => setSaving(false));
  }

  function onFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    if (!file.type.startsWith("image/")) {
      setStatus({ severity: "error", text: "Please choose an image file (PNG, JPG or SVG)." });
      return;
    }
    resizeLogo(file)
      .then((dataUrl) => persist(dataUrl, "Logo updated. Players will see it next to your events."))
      .catch(() =>
        setStatus({ severity: "error", text: "We couldn't read that image. Try a PNG or JPG." })
      );
  }

  return (
    <Stack spacing={1.5} data-testid={"club-logo-upload-" + clubId}>
      <Typography variant="h6">Club Logo</Typography>
      <Typography variant="body2" color="text.secondary">
        Your logo is shown next to each of your events so players can spot your club at a
        glance. A square image works best.
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ position: "relative" }}>
          <ClubLogo clubId={clubId} name={clubName} logo={logo} size={72} />
          {saving && (
            <CircularProgress
              size={80}
              sx={{ position: "absolute", top: -4, left: -4, color: "secondary.main" }}
            />
          )}
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            disabled={saving}
            onClick={() => inputRef.current?.click()}
            data-testid={"club-logo-upload-button-" + clubId}
          >
            {logo ? "Change logo" : "Upload logo"}
          </Button>
          {logo && (
            <Button
              color="inherit"
              startIcon={<DeleteOutlineIcon />}
              disabled={saving}
              onClick={() => persist(null, "Logo removed.")}
              data-testid={"club-logo-remove-" + clubId}
            >
              Remove
            </Button>
          )}
        </Stack>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          hidden
          onChange={onFileSelected}
          data-testid={"club-logo-input-" + clubId}
        />
      </Stack>
      {status && <Alert severity={status.severity}>{status.text}</Alert>}
    </Stack>
  );
}

export default ClubLogoUpload;
